import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowDownIcon } from '@heroicons/react/24/outline'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts'
import { fetchMetrics } from '@/services/api'

const timeRanges = [
  { label: 'Last 24 hours', value: '24h' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
]

const requestsOverTime = [
  { date: '2023-12-04', requests: 12000, errors: 45, responseTime: 120 },
  { date: '2023-12-05', requests: 15000, errors: 32, responseTime: 110 },
  { date: '2023-12-06', requests: 18000, errors: 28, responseTime: 95 },
  { date: '2023-12-07', requests: 22000, errors: 15, responseTime: 105 },
  { date: '2023-12-08', requests: 25000, errors: 8, responseTime: 88 },
  { date: '2023-12-09', requests: 19000, errors: 12, responseTime: 92 },
  { date: '2023-12-10', requests: 21000, errors: 18, responseTime: 98 },
]

const endpointUsage = [
  { name: '/v1/users', value: 45, color: '#3b82f6' },
  { name: '/v1/products', value: 30, color: '#10b981' },
  { name: '/v1/orders', value: 15, color: '#f59e0b' },
  { name: '/v1/auth', value: 10, color: '#ef4444' },
]

const geographicData = [
  { country: 'United States', requests: 45230, percentage: 42 },
  { country: 'United Kingdom', requests: 23100, percentage: 21 },
  { country: 'Germany', requests: 18900, percentage: 17 },
  { country: 'Canada', requests: 12400, percentage: 11 },
  { country: 'Australia', requests: 9800, percentage: 9 },
]

const statusCodeData = [
  { code: '200', count: 98500, color: '#10b981' },
  { code: '400', count: 1200, color: '#f59e0b' },
  { code: '401', count: 800, color: '#ef4444' },
  { code: '404', count: 600, color: '#6b7280' },
  { code: '500', count: 150, color: '#dc2626' },
]

export default function Analytics() {
  const [selectedTimeRange, setSelectedTimeRange] = useState('7d')

  const { isLoading } = useQuery({
    queryKey: ['metrics', selectedTimeRange],
    queryFn: () => fetchMetrics(),
  })

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-80 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="mt-2 text-sm text-gray-700">
            Detailed insights into your API usage and performance
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="input w-auto"
          >
            {timeRanges.map((range) => (
              <option key={range.value} value={range.value}>
                {range.label}
              </option>
            ))}
          </select>
          
          <button className="btn-secondary">
            <ArrowDownIcon className="h-4 w-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Requests Over Time */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Requests & Response Time Over Time
        </h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={requestsOverTime}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Bar yAxisId="left" dataKey="requests" fill="#3b82f6" name="Requests" />
              <Line 
                yAxisId="right" 
                type="monotone" 
                dataKey="responseTime" 
                stroke="#10b981" 
                strokeWidth={2}
                name="Response Time (ms)"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Endpoint Usage */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Endpoint Usage Distribution
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={endpointUsage}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}%`}
                >
                  {endpointUsage.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status Codes */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            HTTP Status Codes
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusCodeData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="code" type="category" />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Geographic Distribution */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Geographic Distribution</h3>
        </div>
        <div className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Country
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requests
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Percentage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Distribution
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {geographicData.map((country, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {country.country}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {country.requests.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {country.percentage}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full"
                        style={{ width: `${country.percentage}%` }}
                      ></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Error Analysis */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Error Rate Trend
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={requestsOverTime}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Line 
                type="monotone" 
                dataKey="errors" 
                stroke="#ef4444" 
                strokeWidth={2}
                name="Errors"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}
